import { useEffect, useState } from "react";
import { Compass } from "lucide-react";
import api from "@/lib/api";
import ThinkingLoader from "@/components/ThinkingLoader";

/**
 * Onboarding tour funnel — per-step retention & drop-off over the last N days.
 *
 * Data source: GET /api/admin/onboarding-funnel?days=30&variant=main
 * Emits from OnboardingTour.jsx on every `view` / `next` / `skip` / `complete`.
 */
export default function AdminOnboardingFunnelWidget() {
  const [days, setDays] = useState(30);
  const [variant, setVariant] = useState("main");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/admin/onboarding-funnel?days=${days}&variant=${variant}`)
      .then((r) => { if (!cancel) { setData(r.data); setErr(null); } })
      .catch((e) => { if (!cancel) setErr(e?.response?.data?.detail || "Kon funnel niet laden"); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [days, variant]);

  const top = data?.steps?.[0]?.views || 0;

  return (
    <section className="mt-4" data-testid="admin-onboarding-funnel">
      <div className="ed-card p-6">
        <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
          <div>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 inline-flex items-center gap-1.5">
              <Compass size={11} strokeWidth={1.5} /> Onboarding tour funnel
            </p>
            <p className="font-serif text-2xl mt-1" data-testid="onboarding-funnel-title">
              {data?.starters || 0} gebruikers startten de tour
              <span className="ml-3 font-mono text-xs text-neutral-500">
                · {data?.completion_rate ?? 0}% klaar · {data?.total_skips ?? 0} skips
              </span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              data-testid="onboarding-funnel-days"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border border-neutral-300 px-2 py-1 font-mono text-[0.7rem] bg-white"
            >
              <option value={7}>Laatste 7 dagen</option>
              <option value={30}>Laatste 30 dagen</option>
              <option value={90}>Laatste 90 dagen</option>
            </select>
            <select
              data-testid="onboarding-funnel-variant"
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              className="border border-neutral-300 px-2 py-1 font-mono text-[0.7rem] bg-white"
            >
              <option value="main">Hoofd-tour</option>
              <option value="partner">Partner-tour</option>
            </select>
          </div>
        </div>

        {loading && <ThinkingLoader stage="generic" testId="admin-onboarding-funnel-thinking" />}
        {err && <p className="font-mono text-xs text-rose-600" data-testid="onboarding-funnel-error">{err}</p>}

        {!loading && !err && data?.steps?.length > 0 && (
          <div className="space-y-2.5">
            {data.steps.map((s) => {
              const widthPct = Math.max(2, Math.min(100, top ? (s.views / top) * 100 : 0));
              const dropClass = s.drop_off_pct >= 50
                ? "text-rose-600"
                : s.drop_off_pct >= 25 ? "text-amber-600" : "text-neutral-500";
              return (
                <div
                  key={s.step_index}
                  className="flex items-center gap-4"
                  data-testid={`onboarding-funnel-row-${s.step_index}`}
                >
                  <div className="w-8 shrink-0 font-mono text-[0.7rem] text-neutral-400">
                    #{s.step_index + 1}
                  </div>
                  <div className="w-52 shrink-0 font-serif text-sm truncate" title={s.title}>
                    {s.title}
                  </div>
                  <div className="flex-1 relative h-7 bg-neutral-100 border border-neutral-200 overflow-hidden">
                    <div
                      className="h-full bg-neutral-900 transition-all duration-500 ease-out"
                      style={{ width: `${widthPct}%` }}
                    />
                    <span
                      className="absolute inset-0 flex items-center px-2 font-mono text-[0.7rem] mix-blend-difference"
                      style={{ color: "#F9F8F6" }}
                    >
                      {s.views} views · {s.skips} skips · {s.completes} done
                    </span>
                  </div>
                  <div className={`w-24 text-right font-mono text-[0.7rem] ${dropClass}`} data-testid={`onboarding-funnel-drop-${s.step_index}`}>
                    -{s.drop_off_pct}%
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && !err && (data?.steps?.length ?? 0) === 0 && (
          <p className="font-mono text-xs text-neutral-500" data-testid="onboarding-funnel-empty">
            Nog geen tour-events in deze periode.
          </p>
        )}
      </div>
    </section>
  );
}
